import type { ReactNode } from 'react';
import { Brand } from './Brand.js';

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint ? (
        <p className="field-hint" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Errors are announced so screen-reader users hear them without refocusing. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="form-error" role="alert">
      {message}
    </p>
  );
}

export function FormNotice({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="form-notice" role="status">
      {message}
    </p>
  );
}

/**
 * `aside` splits the page into the design system's two worlds — the form stays
 * backstage in the house, the aside is the stage. Only sign-in and sign-up
 * pass it: the rest of the auth flow (verify, reset, invite) is mid-task, and
 * a pitch beside a step someone is already taking is just noise.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  aside,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  aside?: ReactNode;
}) {
  const card = (
    <div className="card">
      <Brand />
      <h1>{title}</h1>
      {subtitle ? <p className="muted">{subtitle}</p> : null}
      {children}
      {footer ? <div className="card-footer">{footer}</div> : null}
    </div>
  );

  if (!aside) return <div className="center-page">{card}</div>;

  // Card first in the DOM on purpose: it is the page's job, it holds the only
  // h1, and stacked on a phone the form should not sit below the pitch.
  return (
    <div className="auth-split">
      <div className="auth-house">{card}</div>
      {aside}
    </div>
  );
}
